<template>
  <div class="new-bundle-container">
    <el-header>
      <div class="header-content">
        <h2>{{ isEditing ? 'Edit Bundle' : 'Create New Bundle' }}</h2>
        <div class="button-group">
          <el-button @click="cancel">Cancel</el-button>
          <el-button v-if="isEditing" icon="el-icon-link" :disabled="!bundleForm.bundle_name_url_identifier" @click="copyBundleLink('slug')">Copy slug link</el-button>
          <el-button v-if="isEditing" icon="el-icon-link" :disabled="!bundleForm.bundle_id" @click="copyBundleLink('id')">Copy ID link</el-button>
          <el-button 
            type="primary" 
            @click="saveBundle"
            :loading="saving"
          >
            {{ isEditing ? 'Save' : 'Create' }}
          </el-button>
        </div>
      </div>
    </el-header>

    <el-main>
      <div class="form-container">
        <el-form 
          ref="bundleForm"
          :model="bundleForm"
          :rules="rules"
          label-position="top"
        >
          <el-tabs v-model="activeTab">
            <el-tab-pane label="Basic Info" name="basic">
              <el-form-item label="Bundle Name" prop="bundle_name">
                <el-input 
                  v-model="bundleForm.bundle_name"
                  placeholder="Enter bundle name"
                ></el-input>
              </el-form-item>

              <el-form-item label="Description" prop="bundle_description">
                <template #label>
                  <div style="display: flex; align-items: center; gap: 10px">
                    <span>Description</span>
                    <el-button
                      v-if="isSupremeUser"
                      type="primary"
                      size="small"
                      icon="el-icon-magic-stick"
                      @click="showAIDialog('Bundle Description Generation')"
                    >AI Generate</el-button>
                  </div>
                </template>
                <el-input
                  type="textarea"
                  v-model="bundleForm.bundle_description"
                  :rows="4"
                  placeholder="Enter bundle description"
                ></el-input>
              </el-form-item>

              <el-form-item label="Status" prop="bundle_status">
                <el-select v-model="bundleForm.bundle_status" style="width: 100%">
                  <el-option label="Private" value="PRIVATE"></el-option>
                  <el-option label="Published" value="PUBLISHED"></el-option>
                </el-select>
              </el-form-item>

            </el-tab-pane>

            <el-tab-pane label="Collections" name="collections">
              <div class="collections-section">
                <div class="collections-header">
                  <h3>Included Collections</h3>
                  <el-button 
                    type="primary" 
                    icon="el-icon-plus"
                    @click="showCollectionSelector"
                  >
                    Add Collection
                  </el-button>
                </div>

                <div class="collections-list">
                  <draggable 
                    v-model="bundleForm.bundle_collections"
                    @start="drag=true"
                    @end="drag=false"
                    handle=".drag-handle"
                    @change="updateCollectionOrder"
                  >
                    <el-card 
                      v-for="collection_id in bundleForm.bundle_collections" 
                      :key="collection_id"
                      class="collection-card"
                    >
                      <div class="collection-item">
                        <el-button 
                          class="drag-handle" 
                          icon="el-icon-rank"
                          type="text"
                        ></el-button>
                        <el-image 
                          :src="getBundleCollection(collection_id).cover_url" 
                          fit="cover"
                          class="collection-cover"
                        >
                          <div slot="error" class="image-placeholder">
                            <i class="el-icon-picture-outline"></i>
                          </div>
                        </el-image>
                        <div class="collection-info">
                          <h4>{{ getBundleCollection(collection_id).title || 'Untitled Collection' }}</h4>
                          <p>{{ getBundleCollection(collection_id).description || 'Collection details are not available yet.' }}</p>
                        </div>
                        <el-button 
                          type="danger" 
                          icon="el-icon-delete"
                          circle
                          @click="removeCollection(collection_id)"
                        ></el-button>
                      </div>
                    </el-card>
                  </draggable>
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane label="Pricing & Purchase Access" name="pricing">
              <div class="pricing-container">
                <el-form-item label="Price (USD)" prop="bundle_usd_price">
                  <el-input-number 
                    v-model="bundleForm.bundle_usd_price"
                    :min="0"
                    :precision="2"
                    :step="0.01"
                    style="width: 200px"
                  ></el-input-number>
                </el-form-item>
                <el-form-item label="Allow Single Purchase">
                  <el-switch
                    v-model="bundleForm.single_purchase_enabled"
                    active-color="#409eff"
                    inactive-color="#dcdfe6"
                  ></el-switch>
                </el-form-item>
                <el-form-item label="Bundle content status">
                  <el-radio-group v-model="bundleForm.has_future_collections_planned">
                    <el-radio :label="false">Complete — no more collections planned</el-radio>
                    <el-radio :label="true">Growing — more collections planned</el-radio>
                  </el-radio-group>
                  <div v-if="bundleForm.has_future_collections_planned === null" class="pricing-hint">
                    Set this for existing bundles so learners can see whether more collections are planned.
                  </div>
                  <div class="pricing-hint">
                    This tells learners whether the bundle’s collection list is expected to grow.
                  </div>
                </el-form-item>
                <el-form-item
                  label="Future additions with one-time purchase"
                >
                  <el-radio-group
                    v-model="bundleForm.single_purchase_includes_future_collections"
                    :disabled="!bundleForm.single_purchase_enabled || !bundleForm.has_future_collections_planned"
                  >
                    <el-radio :label="false">Only collections included at purchase time</el-radio>
                    <el-radio :label="true">Include collections added later</el-radio>
                  </el-radio-group>
                  <div v-if="!bundleForm.single_purchase_enabled || !bundleForm.has_future_collections_planned" class="pricing-hint">
                    This option is available when one-time purchase is enabled and more collections are planned.
                  </div>
                </el-form-item>
              </div>
            </el-tab-pane>

            <el-tab-pane label="Examination" name="examination" v-if="isEditing">
              <BundleExamConfigurationEditor
                :bundle-id="bundleForm.bundle_id"
                :collection-count="bundleForm.bundle_collections.length"
              />
            </el-tab-pane>

            <el-tab-pane label="Settings" name="settings">
              <div class="settings-container">
                <label class="settings-label">Bundle Publicity
  <el-tooltip content="You can switch the publicity of your bundle. If private, your bundle will be unlisted from our market and its details will be hidden" placement="top">
    <i class="el-icon-info" style="margin-left: 6px; color: #909399; cursor: pointer;"></i>
  </el-tooltip>
</label>
                <el-switch
                  v-model="bundleForm.bundle_status"
                  active-value="PUBLISHED"
                  inactive-value="PRIVATE"
                  active-text="Published"
                  inactive-text="Private"
                  class="status-switch"
                />
              </div>
            </el-tab-pane>

          </el-tabs>
        </el-form>
      </div>
    </el-main>

    <!-- Collection Selector Dialog -->
    <el-dialog
      :visible.sync="collectionSelectorVisible"
      width="70%"
      custom-class="collection-selector-dialog"
      :close-on-click-modal="false"
    >
      <div slot="title" class="selector-title">
        <span>Select Collections</span>
        <span class="selector-title-actions">
          <span v-if="selectedCollections.length" class="selector-count">{{ selectedCollections.length }} selected</span>
          <el-button size="small" :disabled="saving" @click="collectionSelectorVisible = false">Cancel</el-button>
          <el-button size="small" type="primary" :disabled="!selectedCollections.length" @click="addSelectedCollections">Add Selected</el-button>
        </span>
      </div>
      <el-tabs v-model="collectionScope" @tab-click="changeCollectionScope">
        <el-tab-pane label="My collections" name="own" />
        <el-tab-pane label="Public collections" name="public" />
      </el-tabs>
      <el-input
        v-if="collectionScope === 'public'"
        v-model="collectionSearch"
        placeholder="Search public collections by title"
        clearable
        @keyup.enter.native="showCollectionSelector()"
      >
        <el-button slot="append" icon="el-icon-search" @click="showCollectionSelector()" />
      </el-input>
      <div class="selector-body">
        <el-table
          v-loading="loadingCollections"
          :data="availableCollections"
          row-key="entry_id"
          @selection-change="handleSelectionChange"
        >
          <el-table-column
            type="selection"
            width="55"
            :reserve-selection="true">
          </el-table-column>
          <el-table-column label="Cover" width="90">
            <template slot-scope="scope">
              <img v-if="scope.row.cover_url" :src="scope.row.cover_url" class="selector-cover" />
              <span v-else class="selector-cover-empty">—</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="title"
            label="Title">
            <template slot-scope="scope">
              {{ scope.row.title }}{{ scope.row.version_identifier ? ` (${scope.row.version_identifier})` : '' }}
            </template>
          </el-table-column>
          <el-table-column label="Owner" width="180">
            <template slot-scope="scope">
              {{ scope.row.own ? 'You' : (scope.row.owner_name || 'Unknown') }}
            </template>
          </el-table-column>
          <el-table-column
            prop="usd_price"
            label="Price"
            width="90">
            <template slot-scope="scope">
              ${{ scope.row.usd_price }}
            </template>
          </el-table-column>
          <el-table-column
            prop="status"
            label="Status"
            width="120">
            <template slot-scope="scope">
              <el-tag :type="collectionStatusTagType(scope.row.status)" size="small">
                {{ collectionStatusLabel(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
        <div v-if="moreCollections" class="selector-load-more">
          <el-button size="small" :loading="loadingCollections" @click="showCollectionSelector(true)">Load more</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- AI Dialog -->
    <AIDialog
      :visible.sync="aiDialogVisible"
      :title="aiDialogTitle"
      :task-type="aiTaskType"
      :initial-input="aiDialogInitialInput"
      :context-string="aiContextString"
      @use="handleAIOutput"
    />
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'
import { copyPublicContentUrl, publicContentUrl } from '@/utils/publicContentLinks'
import draggable from 'vuedraggable'
import AIDialog from '@/components/AIDialog.vue'
import BundleExamConfigurationEditor from '@/components/Examination/BundleExamConfigurationEditor.vue'

export default {
  name: 'NewBundle',
  components: {
    draggable,
    AIDialog,
    BundleExamConfigurationEditor
  },
  data() {
    return {
      collectionSearch: '',
      collectionScope: 'own',
      collectionSkip: 0,
      moreCollections: false,
      bundleForm: {
        bundle_id: "",
        bundle_name: '',
        bundle_description: '',
        bundle_usd_price: 0,
        single_purchase_enabled: false,
        has_future_collections_planned: false,
        single_purchase_includes_future_collections: false,
        bundle_status: 'DRAFT',
        bundle_collections: [],
        collection_array: {}
      },
      rules: {
        bundle_name: [
          { required: true, message: 'Please enter bundle name', trigger: 'blur' }
        ],
        bundle_description: [
          { required: true, message: 'Please enter bundle description', trigger: 'blur' }
        ]
      },
      activeTab: 'basic',
      saving: false,
      collectionSelectorVisible: false,
      availableCollections: [],
      selectedCollections: [],
      loadingCollections: false,
      drag: false,
      aiDialogVisible: false,
      aiDialogTitle: '',
      aiTaskType: '',
      aiDialogInitialInput: '',
      aiContextString: '',
    }
  },
  computed: {
    isEditing() {
      return this.$route.params.id !== undefined
    },
    isSupremeUser() {
      return this.$store.state.user_info.creator_functionality_level === 'Supreme'
    }
  },
  watch: {
    'bundleForm.single_purchase_enabled'(enabled) {
      if (!enabled) {
        this.bundleForm.single_purchase_includes_future_collections = false
      }
    },
    'bundleForm.has_future_collections_planned'(planned) {
      if (!planned) {
        this.bundleForm.single_purchase_includes_future_collections = false
      }
    }
  },
  methods: {
    collectionStatusLabel(status) {
      if (['PREORDER', 'TEASER', 'REQUEST'].includes(status)) return 'Pre-order'
      if (status === 'PUBLISHED') return 'Published'
      if (status === 'PRIVATE') return 'Private'
      return status || 'Private'
    },
    collectionStatusTagType(status) {
      if (['PREORDER', 'TEASER', 'REQUEST'].includes(status)) return 'warning'
      return status === 'PUBLISHED' ? 'success' : 'info'
    },
    toBoolean(value) {
      return value === true || value === 1 || value === '1' || value === 'true'
    },
    getObjectId(value) {
      return value && (value.$oid || value.$id) ? (value.$oid || value.$id) : value
    },
    normalizeBundleForm(bundle) {
      const singlePurchaseEnabled = this.toBoolean(
        bundle.single_purchase_enabled !== undefined
          ? bundle.single_purchase_enabled
          : bundle.bundle_single_purchase_enabled
      )
      return {
        bundle_id: this.getObjectId(bundle._id) || bundle.id || bundle.bundle_id || '',
        bundle_name: bundle.bundle_name || '',
        bundle_name_url_identifier: bundle.bundle_name_url_identifier || '',
        bundle_description: bundle.bundle_description || '',
        bundle_usd_price: Number(bundle.bundle_usd_price) || 0,
        single_purchase_enabled: singlePurchaseEnabled,
        has_future_collections_planned: bundle.has_future_collections_planned === undefined
          ? null
          : this.toBoolean(bundle.has_future_collections_planned),
        single_purchase_includes_future_collections: singlePurchaseEnabled && this.toBoolean(bundle.has_future_collections_planned) && this.toBoolean(
          bundle.single_purchase_includes_future_collections !== undefined
            ? bundle.single_purchase_includes_future_collections
            : bundle.bundle_single_purchase_includes_future_collections
        ),
        bundle_status: bundle.bundle_status || 'DRAFT',
        bundle_collections: Array.isArray(bundle.bundle_collections) ? bundle.bundle_collections : [],
        collection_array: bundle.collection_array || {}
      }
    },
    buildBundlePayload() {
      const singlePurchaseEnabled = this.toBoolean(this.bundleForm.single_purchase_enabled)
      const hasFutureCollectionsPlanned = this.bundleForm.has_future_collections_planned === null
        ? null
        : this.toBoolean(this.bundleForm.has_future_collections_planned)
      const includeFutureCollections = singlePurchaseEnabled && hasFutureCollectionsPlanned === true && this.toBoolean(this.bundleForm.single_purchase_includes_future_collections)
      const payload = {
        ...this.bundleForm,
        single_purchase_enabled: singlePurchaseEnabled,
        single_purchase_includes_future_collections: includeFutureCollections,
        bundle_single_purchase_enabled: singlePurchaseEnabled,
        bundle_single_purchase_includes_future_collections: includeFutureCollections,
        user_id: this.$store.state.user_id
      }
      if (hasFutureCollectionsPlanned !== null) payload.has_future_collections_planned = hasFutureCollectionsPlanned
      else delete payload.has_future_collections_planned
      return payload
    },
    async copyBundleLink(format) {
      try {
        await copyPublicContentUrl(publicContentUrl('bundle', this.bundleForm, format))
        this.$message.success(`${format === 'slug' ? 'Slug' : 'ID'} link copied to clipboard`)
      } catch (error) {
        this.$message.error('Failed to copy link')
      }
    },
    getBundleCollection(collectionId) {
      return (this.bundleForm.collection_array && this.bundleForm.collection_array[collectionId]) || {}
    },
    async saveBundle() {
      try {
        const authResponse = await postRequest('whoami', { user_id: this.$store.state.user_id })
        const authResult = authResponse && authResponse.data ? authResponse.data : {}
        const signedInUserId = authResult.data && authResult.data.user_id
        if (authResult.code !== 0 || !signedInUserId) {
          this.$message.error('Your sign-in has expired. Please sign in again before saving this bundle.')
          return
        }
        if (signedInUserId !== this.$store.state.user_id) {
          this.$message.error('Your active sign-in is a different account. Sign out, then sign in as this bundle’s owner before saving.')
          return
        }
        await this.$refs.bundleForm.validate()
        
        this.saving = true
        const response = await postRequest('updateCollectionBundle', this.buildBundlePayload())

        if (response.data.code === 0) {
          this.$message.success('Bundle saved successfully')
          this.$router.go(-1)
        } else {
          this.$message.error(response.data.msg || 'Failed to save bundle')
        }
      } catch (error) {
        console.error('Error saving bundle:', error)
        this.$message.error('Failed to save bundle')
      } finally {
        this.saving = false
      }
    },
    cancel() {
      this.$router.go(-1)
    },
    async showCollectionSelector(loadMore = false) {
      this.collectionSelectorVisible = true
      if (loadMore !== true) this.collectionSkip = 0
      this.loadingCollections = true
      try {
        const response = await postRequest('searchBundleCollections', {
          scope: this.collectionScope,
          search: this.collectionScope === 'public' ? this.collectionSearch : '',
          skip: this.collectionSkip,
          user_id: this.$store.state.user_id
        })

        if (response.data.code === 0) {
          const candidates = (response.data.data.items || []).map(item => {
            const entryId = item._id && (item._id.$oid || item._id.$id) ? (item._id.$oid || item._id.$id) : String(item._id || '')
            return { ...item, entry_id: entryId || `entry-${Math.random()}` }
          })
          this.moreCollections = response.data.data.has_more
          const next = candidates.filter(collection =>
            !this.bundleForm.bundle_collections.some(
              bundleCollection_id => bundleCollection_id === (collection._id.$oid || collection._id.$id)
            )
          )
          this.availableCollections = loadMore === true ? [...this.availableCollections, ...next] : next
          this.collectionSkip += candidates.length
          this.collectionSelectorVisible = true
        } else {
          this.$message.error(response.data.msg || 'Failed to fetch collections')
        }
      } catch (error) {
        console.error('Error fetching collections:', error)
        this.$message.error('Failed to fetch collections')
      } finally {
        this.loadingCollections = false
      }
    },
    changeCollectionScope() {
      this.selectedCollections = []
      this.showCollectionSelector()
    },
    handleSelectionChange(val) {
      console.log('val:', val)
      this.selectedCollections = val
    },
    addSelectedCollections() {
      const known = new Set(this.bundleForm.bundle_collections);
      this.selectedCollections.forEach(collection => {
        const collectionId = (collection._id.$oid || collection._id.$id);
        if (known.has(collectionId)) return;
        known.add(collectionId);
        this.bundleForm.bundle_collections.push(collectionId);
        this.bundleForm.collection_array[collectionId] = collection;
      });
      this.collectionSelectorVisible = false;
      this.selectedCollections = [];
    },
    removeCollection(collectionId) {
      this.bundleForm.bundle_collections = this.bundleForm.bundle_collections.filter(
        id => id !== collectionId
      );
      delete this.bundleForm.collection_array[collectionId];
    },
    async fetchBundleData() {
      if (!this.isEditing) return
      
      try {
        const response = await postRequest('getCollectionBundle', {
          bundle_id: this.$route.params.id,
          user_id: this.$store.state.user_id
        })

        if (response.data.code === 0) {
          const bundle = response.data.data

          // Verify the bundle belongs to the current user
          const currentUserId = this.$store.state.user_id;
          if (bundle.can_edit === false || (bundle.can_edit === undefined && bundle.user_id && bundle.user_id !== currentUserId)) {
            // Bundle belongs to a different user, clear stored ID and reset
            this.$message.warning("This bundle belongs to another user. Starting a new bundle.");
            localStorage.removeItem('editBundleId');
            this.$store.dispatch('setTempBundleId', null);
            return;
          }

          this.bundleForm = this.normalizeBundleForm(bundle)
        }
      } catch (error) {
        this.$message.error('Failed to fetch bundle data')
        // Clear invalid bundle ID
        localStorage.removeItem('editBundleId');
        this.$store.dispatch('setTempBundleId', null);
      }
    },
    updateCollectionOrder() {
      console.log('Collections reordered:', this.bundleForm.bundle_collections);
    },
    showAIDialog(field) {
      
      this.aiDialogTitle = field;
      this.aiTaskType = field;
      this.aiDialogInitialInput = this.bundleForm.bundle_description || '';
      
      // Prepare context for AI
      const params = {
        bundle_id: this.bundleForm.bundle_id,
        bundle_name: this.bundleForm.bundle_name,
        bundle_description: this.bundleForm.bundle_description,
        user_id: this.$store.state.user_id,
        collections: Object.values(this.bundleForm.collection_array).map(collection => ({
          title: collection.title,
          description: collection.description
        }))
      };
      this.aiContextString = JSON.stringify(params);
      this.aiDialogVisible = true;
    },
    handleAIOutput(output) {
      if (this.aiTaskType == 'Bundle Description Generation') {
        this.bundleForm.bundle_description = output;
        this.$message.success('AI content applied successfully');
      }
      this.aiDialogVisible = false;
    }
  },
  mounted() {
    this.fetchBundleData()
  }
}
</script>

<style scoped>
.new-bundle-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.form-container {
  background: white;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.collections-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.collection-card {
  margin-bottom: 15px;
}

.collection-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.collection-cover {
  width: 100px;
  height: 140px;
  border-radius: 4px;
  overflow: hidden;
}

.collection-info {
  flex: 1;
}

.collection-info h4 {
  margin: 0 0 10px 0;
}

.collection-info p {
  margin: 0;
  color: #666;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  color: #909399;
}

.button-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.drag-handle {
  cursor: move;
  color: #909399;
  padding: 0 8px;
}

.drag-handle:hover {
  color: #409EFF;
}

.pricing-hint {
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 8px;
}

.settings-container {
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.settings-label {
  font-weight: 500;
  font-size: 16px;
  margin-right: 10px;
}

.status-switch {
  margin-left: 10px;
}

</style>

<style>
/* Collection selector dialog renders in body, so these styles are unscoped. */
.collection-selector-dialog .el-dialog__body { padding-top: 10px; }
.collection-selector-dialog .selector-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-right: 20px;
}
.collection-selector-dialog .selector-title-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: normal;
}
.collection-selector-dialog .selector-count { font-size: 12px; color: #6b7a8d; }
.collection-selector-dialog .selector-body { max-height: 52vh; overflow-y: auto; }
.collection-selector-dialog .selector-cover {
  width: 56px;
  height: 34px;
  object-fit: cover;
  border-radius: 4px;
  display: block;
  background: #f0f3f7;
}
.collection-selector-dialog .selector-cover-empty { color: #b6c2cf; }
.collection-selector-dialog .selector-load-more { margin-top: 10px; text-align: center; }
</style> 
